// Ch78 · THE MOVE-IN CALENDAR — the Leads page's hero.
//
// A year of stacked bars over a real calendar month. Height is how many people
// move in that month, the stack is the scorer's read; click a bar to jump.
// Below it every person is a pill on the day they said they are moving,
// coloured by that same read, ringed green when they asked to be texted.
// Month · Week · Timeline — Timeline is the existing MoveInBoard, unchanged.
//
// Model lives in lib/leadsCalendar.ts (pure, fixture-tested); this file lays it
// out and forwards clicks. Nobody is hidden: the three gutters under the grid
// hold the people whose dates are awkward, and each opens in place.

import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoDot from "./InfoDot";
import { compactMoney, type BoardPerson } from "../../lib/moveInBoard";
import {
  buildCalendar,
  buildGutters,
  buildYearStrip,
  shiftCalendarAnchor,
  snapCalendarAnchor,
} from "../../lib/leadsCalendar";
import { toneColor, type TimelineTone } from "../../lib/leadsTimeline";

// Ch78 · Grant (2026-09-10): "i think we can remove month, week, timeline
// right? kinda useless -- just make it always month".
//
// Right, and for a worse reason than clutter: the Timeline branch was fed the
// RAW leads/clients props, not the filtered rows the rest of the page reads.
// Searching "aus" put "showing 0 of 70 · 0 moving in · $0" directly above a
// timeline announcing "23 moving in · $43.2k budget" and dumping 238 past-due
// pills. The same two-populations bug the filter bar was built to end, hiding
// in the one branch that took a different input.
//
// A month grid is what anybody actually reads a move-in date off. Week showed
// less of the same thing; Timeline showed a different set entirely.

export interface LeadsMoveInCalendarProps {
  /** The Leads-page population, already deduped through the lead→client link. */
  people: BoardPerson[];
  /** Keys the work list has ruled expired — one definition, read not recomputed. */
  expiredKeys: Set<string>;
  onOpen: (person: BoardPerson) => void;
  /**
   * "Take me to this person." The grid pages to their move-in month and rings
   * their pill. Carries the day rather than just the key so the calendar never
   * has to search the population for somebody it may have filtered out.
   */
  focus?: { key: string; ms: number } | null;
  /**
   * Drag a pill onto a day. CLIENTS ONLY, and on purpose: the living profile
   * records a move-in as an append-only fact carrying its source, so an
   * operator's correction sits BESIDE the renter's own words. A lead has no
   * such ledger — `leads.move_in_date` is what the renter typed, and quietly
   * replacing it would destroy exactly the provenance the living profile
   * exists to keep. Lead pills say so instead of silently refusing.
   */
  // Ch78 · Grant (2026-09-10): "theres no need to drag and drop for move-in
  // date - that just opens us up to doing that accidentially - we should be
  // able to edit that in client/lead modal". A calendar you can shove people
  // around on makes a WRITE out of a stray mouse gesture, and a move-in date is
  // the one field the whole business schedules against. Editing it is now a
  // deliberate act inside the record, where it can be seen and undone.
  now?: Date;
}

const MAX_PILLS_PER_DAY = 3;
/** Same spelling the grid header uses, so the jump names the month you land on. */
const MONTH_LABEL = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });
/** Production has 128 past-due people. Opening that gutter used to render 128
 *  chips in one wall; the newest-passed few are the ones worth looking at. */
const MAX_GUTTER_CHIPS = 24;
const STRIP_MAX_H = 62;
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TONES: readonly TimelineTone[] = ["real", "uncertain", "fake", "unscored"];
const TONE_LABEL: Record<TimelineTone, string> = {
  real: "Real",
  uncertain: "Uncertain",
  fake: "Fake",
  unscored: "Not scored",
};

const HERO_INFO =
  "The one date the business runs on. Colour is the scorer's read — green real, amber uncertain, " +
  "red fake, grey not scored; a green ring means they asked to be texted; the number is stated budget. " +
  "The bars are the year: height is how many move in that month, past months dimmed. Click a month to " +
  "jump, a person for their record, \"+N more\" to open a crowded day. Free text like \"August\" is never " +
  "guessed onto a day — those people sit in the gutter below with their own words.";

/** "Christine P." — the pill has room for a first name and an initial. */
function shortName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "Unknown";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

export default function LeadsMoveInCalendar({
  people,
  expiredKeys,
  onOpen,
  focus,
  now,
}: LeadsMoveInCalendarProps) {
  const theme = useTheme();
  const today = useMemo(() => now ?? new Date(), [now]);

  const [anchor, setAnchor] = useState<Date>(() => snapCalendarAnchor(today, "month"));
  const [expandedDay, setExpandedDay] = useState<number | null>(null);

  // Paging to the focused person is an EFFECT of being asked, not of rendering:
  // keyed on the day so picking the same person twice re-pages after you have
  // clicked away, and picking nobody leaves the anchor exactly where it was.
  React.useEffect(() => {
    if (!focus) return;
    setExpandedDay(focus.ms);
    setAnchor(snapCalendarAnchor(new Date(focus.ms), "month"));
  }, [focus]);
  const [openGutter, setOpenGutter] = useState<"pastDue" | "noDate" | "expired" | null>(null);
  // The dragged key lives in a REF as well as state: the drop target must be
  // able to read it during the same gesture, before React has re-rendered.

  const model = useMemo(
    () => buildCalendar(people, { anchor, mode: "month", now: today }),
    [people, anchor, today],
  );
  const strip = useMemo(
    () =>
      buildYearStrip(people, {
        year: anchor.getFullYear(),
        anchorMonth: anchor.getMonth(),
        anchorYear: anchor.getFullYear(),
        now: today,
      }),
    [people, anchor, today],
  );
  // DEFECT C · a blank grid is not an answer.
  //
  // Park on a month nobody moves in — six clicks forward, or a year-strip bar —
  // and the calendar rendered 35 empty cells with no words at all, while the
  // filter bar above said "218 people". That reads as broken software, not as
  // "nobody moves in March 2027", and it is the likeliest thing Grant meant by
  // "the calendar still seems glitched".
  //
  // So an empty month says so, and points at the nearest month that ISN'T:
  // the answer to "where is everyone?" is one click, not six.
  const nearestPopulated = useMemo(() => {
    const anchorMs = anchor.getTime();
    let best: { ms: number; label: string; count: number } | null = null;
    const byMonth = new Map<number, number>();
    for (const p of people) {
      if (p.dateMs === null) continue;
      const d = new Date(p.dateMs);
      const key = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
      byMonth.set(key, (byMonth.get(key) ?? 0) + 1);
    }
    // "Where is everyone?" means the WORK, and the work is upcoming move-ins.
    // Nearest-to-the-anchor was the obvious rule and the wrong one: paged back
    // to January it offered July — one expired fake lead — over September, which
    // holds four real people and is the month we are actually in. So: the
    // soonest populated month from TODAY forward, and only if there is nothing
    // ahead do we look back.
    const todayMonthMs = new Date(today.getFullYear(), today.getMonth(), 1).getTime();
    const pick = (predicate: (ms: number) => boolean, rank: (ms: number) => number) => {
      let found: { ms: number; label: string; count: number } | null = null;
      for (const [ms, count] of byMonth) {
        if (ms === anchorMs || !predicate(ms)) continue;
        if (!found || rank(ms) < rank(found.ms)) {
          found = { ms, count, label: MONTH_LABEL.format(new Date(ms)) };
        }
      }
      return found;
    };
    best =
      pick((ms) => ms >= todayMonthMs, (ms) => ms - todayMonthMs) ??
      pick(() => true, (ms) => Math.abs(ms - anchorMs));
    return best;
  }, [people, anchor, today]);

  const gutters = useMemo(() => buildGutters(people, expiredKeys, today), [people, expiredKeys, today]);
  const stripMax = Math.max(1, ...strip.map((m) => m.count));

  const pill = (p: BoardPerson) => {
    const color = toneColor(theme, p.tone);
    return (
      <Tooltip
        key={p.key}
        arrow
        title={`${p.name} · ${p.moveInText ?? "no date"} · ${p.budget === null ? "budget not stated" : compactMoney(p.budget)}${p.textsOk ? " · texts OK" : ""}`}
      >
        <Box
          component="button"
          onClick={() => onOpen(p)}
          data-testid={`cal-pill-${p.key}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            width: "100%",
            px: 0.6,
            py: 0.3,
            borderRadius: 1,
            fontFamily: "inherit",
            fontSize: 11.5,
            fontWeight: 700,
            textAlign: "left",
            color: "text.primary",
            bgcolor: alpha(color, p.tone === "fake" ? 0.1 : 0.16),
            border: `1px solid ${alpha(color, 0.4)}`,
            boxShadow:
              focus?.key === p.key
                ? `0 0 0 2px ${theme.palette.primary.main}`
                : p.textsOk
                  ? `0 0 0 1.5px ${alpha(theme.palette.success.main, 0.55)}`
                  : "none",
            cursor: "pointer",
            opacity: p.tone === "fake" ? 0.72 : 1,
            overflow: "hidden",
          }}
        >
          <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {shortName(p.name)}
          </Box>
          {p.budget !== null && (
            <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, opacity: 0.8, flex: "none" }}>
              {compactMoney(p.budget)}
            </Box>
          )}
        </Box>
      </Tooltip>
    );
  };

  const gutterRow = (
    key: "pastDue" | "noDate" | "expired",
    label: string,
    list: BoardPerson[],
  ) => (
    <Stack direction="row" spacing={0.75} alignItems="center" key={key} data-testid={`cal-gutter-${key}`}>
      <Typography variant="caption" fontWeight={800}>{list.length}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      {list.length > 0 && (
        <Button
          size="small"
          onClick={() => setOpenGutter(openGutter === key ? null : key)}
          sx={{ minWidth: 0, p: 0, fontWeight: 700, fontSize: 12 }}
          data-testid={`cal-gutter-show-${key}`}
        >
          {openGutter === key ? "hide" : "show"}
        </Button>
      )}
    </Stack>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }} data-testid="leads-calendar">
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={800}>Move-in calendar</Typography>
        <InfoDot text={HERO_INFO} testId="cal-info" />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" data-testid="cal-summary">
          {model.count} moving in · {compactMoney(model.budgetTotal)}
        </Typography>
      </Stack>

      {/* The year, as twelve bars. Click one to jump the grid to that month. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
          gap: { xs: 0.5, sm: 0.75 },
          alignItems: "end",
          height: STRIP_MAX_H + 34,
          pb: 0.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
        data-testid="cal-strip"
      >
        {strip.map((m) => (
          <Box
            key={m.index}
            component="button"
            onClick={() => {
              setExpandedDay(null);
              setAnchor(new Date(m.startMs));
            }}
            data-testid={`cal-strip-${m.index}`}
            aria-label={`${m.label}: ${m.count} moving in`}
            sx={{
              display: "grid",
              gridTemplateRows: "1fr auto",
              minWidth: 0,
              gap: 0.4,
              height: "100%",
              px: 0.3,
              pt: 0.5,
              border: "none",
              borderRadius: 1,
              cursor: "pointer",
              fontFamily: "inherit",
              textAlign: "center",
              opacity: m.isPast && !m.isAnchor ? 0.45 : 1,
              bgcolor: m.isAnchor ? alpha(theme.palette.primary.main, 0.16) : "transparent",
            }}
          >
            {/* The count rides ON the bar, not at the top of an empty cell:
                a short month otherwise floats its number half a strip away
                from the thing it counts. */}
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", width: "100%" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.3, color: m.isAnchor ? "text.primary" : "text.secondary" }}>
                {m.count}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column-reverse", width: "100%", borderRadius: "3px 3px 0 0", overflow: "hidden" }}>
                {TONES.map((tone) =>
                  m.byTone[tone] > 0 ? (
                    <Box
                      key={tone}
                      sx={{ height: Math.max(3, (m.byTone[tone] / stripMax) * STRIP_MAX_H), bgcolor: toneColor(theme, tone) }}
                    />
                  ) : null,
                )}
              </Box>
            </Box>
            <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 700, color: m.isAnchor ? "primary.main" : "text.secondary" }}>
              {m.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Grid controls */}
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ my: 1.5 }}>
        <IconButton size="small" aria-label="Previous" data-testid="cal-prev" onClick={() => { setExpandedDay(null); setAnchor((a) => shiftCalendarAnchor(a, "month", -1)); }}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={800} sx={{ minWidth: 160, textAlign: "center" }} data-testid="cal-window">
          {model.label}
        </Typography>
        <IconButton size="small" aria-label="Next" data-testid="cal-next" onClick={() => { setExpandedDay(null); setAnchor((a) => shiftCalendarAnchor(a, "month", 1)); }}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <Button size="small" sx={{ fontWeight: 700 }} data-testid="cal-today" onClick={() => { setExpandedDay(null); setAnchor(snapCalendarAnchor(today, "month")); }}>
          Today
        </Button>
        <Box sx={{ flex: 1 }} />
        {TONES.map((tone) => (
          <Stack key={tone} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: toneColor(theme, tone) }} />
            <Typography variant="caption" color="text.secondary">{TONE_LABEL[tone]}</Typography>
          </Stack>
        ))}
      </Stack>

      {(
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: "hidden",
          }}
          data-testid="cal-grid"
        >
          {DOW.map((d) => (
            <Box key={d} sx={{ p: 1, textAlign: "center", borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.text.primary, 0.03) }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: ".06em", textTransform: "uppercase", fontSize: 10.5 }}>
                {d}
              </Typography>
            </Box>
          ))}
          {model.weeks.flat().map((day) => {
            const expanded = expandedDay === day.ms;
            const shown = expanded ? day.people : day.people.slice(0, MAX_PILLS_PER_DAY);
            return (
              <Box
                key={day.ms}
                data-testid={day.isToday ? "cal-today-cell" : undefined}
                data-day={day.ms}
                sx={{
                  minHeight: 108,
                  p: 0.75,
                  borderRight: `1px solid ${theme.palette.divider}`,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  bgcolor: day.isToday
                    ? alpha(theme.palette.primary.main, 0.08)
                    : !day.inMonth
                      ? alpha(theme.palette.common.black, 0.14)
                      : day.isWeekend
                        ? alpha(theme.palette.text.primary, 0.025)
                        : "transparent",
                  boxShadow: day.isToday ? `inset 0 0 0 1.5px ${theme.palette.primary.main}` : "none",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: day.isToday ? 800 : 600, color: day.isToday ? "primary.main" : day.inMonth ? "text.secondary" : "text.disabled" }}>
                    {day.label}{day.isToday ? " · today" : ""}
                  </Typography>
                  {day.people.length > 0 && (
                    <Typography variant="caption" color="text.disabled">{day.people.length}</Typography>
                  )}
                </Stack>
                <Stack spacing={0.4}>
                  {shown.map(pill)}
                  {!expanded && day.people.length > MAX_PILLS_PER_DAY && (
                    <Box
                      component="button"
                      onClick={() => setExpandedDay(day.ms)}
                      data-testid={`cal-more-${day.ms}`}
                      sx={{ border: "none", bgcolor: "transparent", color: "primary.main", fontSize: 11, fontWeight: 800, cursor: "pointer", p: 0, textAlign: "left", fontFamily: "inherit" }}
                    >
                      +{day.people.length - MAX_PILLS_PER_DAY} more
                    </Box>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}

      {model.count === 0 && people.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }} data-testid="cal-empty-month">
          <Typography variant="body2" color="text.secondary">
            Nobody moves in during {model.label}.
          </Typography>
          {nearestPopulated && (
            <Button
              size="small"
              data-testid="cal-jump-nearest"
              onClick={() => {
                setExpandedDay(null);
                setAnchor(new Date(nearestPopulated.ms));
              }}
              sx={{ fontWeight: 700 }}
            >
              {nearestPopulated.count} move in {nearestPopulated.label} →
            </Button>
          )}
        </Stack>
      )}

      {/* The honest leftovers. Nothing here is deleted; everything opens in place. */}
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
        {gutterRow("pastDue", "past due — move-in passed, not placed", gutters.pastDue)}
        {gutterRow("noDate", "gave words, not a date", gutters.noDate)}
        {gutterRow("expired", `expired — unclaimed, passed ${30}+ days`, gutters.expired)}
      </Stack>
      {openGutter && (
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }} data-testid={`cal-gutter-open-${openGutter}`}>
          {gutters[openGutter].slice(0, MAX_GUTTER_CHIPS).map((p) => (
            <Box
              key={p.key}
              component="button"
              onClick={() => onOpen(p)}
              data-testid={`cal-gutter-person-${p.key}`}
              sx={{
                px: 1,
                py: 0.4,
                borderRadius: 1,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: 600,
                color: "text.primary",
                border: `1px solid ${alpha(toneColor(theme, p.tone), 0.45)}`,
                bgcolor: alpha(toneColor(theme, p.tone), 0.1),
              }}
            >
              {p.name}
              {openGutter === "noDate" && p.moveInText ? ` · “${p.moveInText}”` : ""}
              {openGutter !== "noDate" && p.moveInText ? ` · ${p.moveInText}` : ""}
            </Box>
          ))}
          {gutters[openGutter].length > MAX_GUTTER_CHIPS && (
            <Typography variant="caption" color="text.secondary" data-testid="cal-gutter-more">
              + {gutters[openGutter].length - MAX_GUTTER_CHIPS} more, newest first. Work them from the
              list below, where they are grouped and ordered.
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
}
